/**
 * IndexedDB Extraction & Restoration Helpers
 *
 * Enables persisting IndexedDB data as part of browser storage state.
 * This is critical for apps that store auth tokens in IndexedDB (e.g., Firebase Auth)
 * since Playwright's native storageState() only captures cookies + localStorage.
 *
 * @module indexeddb-helpers
 */

import type { Page, Route } from 'playwright';
import { TIMEOUTS } from './constants.js';

/**
 * JSON-safe key type for serialized IDB entries.
 * IDBValidKey is recursive and causes TS2589 with page.evaluate(),
 * so we use a simplified serializable equivalent.
 */
type SerializedIDBKey = string | number | Array<string | number>;

/**
 * Serialized representation of all IndexedDB databases for an origin.
 */
export type IndexedDBData = {
  databases: Array<{
    name: string;
    version: number;
    objectStores: Array<{
      name: string;
      keyPath: string | string[] | null;
      autoIncrement: boolean;
      entries: Array<{ key: SerializedIDBKey; value: unknown }>;
    }>;
  }>;
};

export type IndexedDBRestoreResult = {
  restoredDatabases: number;
  restoredStores: number;
  restoredEntries: number;
  skippedDatabases: Array<{ name: string; error: string }>;
  skippedStores: Array<{ database: string; store: string; error: string }>;
};

export type IndexedDBRestoreExpectation = {
  databases: number;
  stores: number;
  entries: number;
};

export class IndexedDBRestoreIncompleteError extends Error {
  readonly expectation: IndexedDBRestoreExpectation;
  readonly result: IndexedDBRestoreResult;

  constructor(expectation: IndexedDBRestoreExpectation, result: IndexedDBRestoreResult) {
    const skippedDatabases = result.skippedDatabases.map((database) => database.name).join(', ');
    const skippedStores = result.skippedStores
      .map((store) => `${store.database}/${store.store}`)
      .join(', ');
    const details = [
      skippedDatabases ? `databases=${skippedDatabases}` : null,
      skippedStores ? `stores=${skippedStores}` : null,
    ]
      .filter((value): value is string => value !== null)
      .join('; ');
    super(
      `IndexedDB authentication state restoration incomplete: restored ${result.restoredDatabases}/${expectation.databases} databases, ${result.restoredStores}/${expectation.stores} populated stores, and ${result.restoredEntries}/${expectation.entries} entries${details ? ` (${details})` : ''}`
    );
    this.name = 'IndexedDBRestoreIncompleteError';
    this.expectation = expectation;
    this.result = result;
  }
}

/** Max total serialized size (10MB) to avoid bloating storage state files. */
const MAX_TOTAL_SIZE_BYTES = 10 * 1024 * 1024;
/**
 * The worker event loop can legitimately be delayed for several seconds under
 * concurrent suite load. A 1s page-side timer used to win the race against a
 * successful IndexedDB callback after such a stall, silently dropping Firebase
 * auth state. Keep these bounds above the worker's 5s critical-stall threshold;
 * the caller also applies a whole-restore watchdog scaled to the sequential work.
 */
export const IDB_RESTORE_REQUEST_TIMEOUT_MS = 15_000;
export const IDB_RESTORE_TRANSACTION_TIMEOUT_MS = 15_000;
export const IDB_CAPTURE_STORE_READ_TIMEOUT_MS = 15_000;
export const IDB_RESTORE_WATCHDOG_MIN_MS = 30_000;
export const IDB_RESTORE_WATCHDOG_MAX_MS = 5 * 60_000;
const IDB_RESTORE_WATCHDOG_BUFFER_MS = 5_000;
const IDB_REQUEST_TIMEOUT_MS = IDB_RESTORE_REQUEST_TIMEOUT_MS;
const IDB_TRANSACTION_TIMEOUT_MS = IDB_RESTORE_TRANSACTION_TIMEOUT_MS;
const IDB_STORE_READ_TIMEOUT_MS = IDB_CAPTURE_STORE_READ_TIMEOUT_MS;
const MAX_ENTRIES_PER_STORE = 500;

/**
 * Extract all IndexedDB databases from the current page origin.
 *
 * Uses `indexedDB.databases()` to enumerate databases, then reads all object stores
 * and entries. Binary data (ArrayBuffer, typed arrays) is serialized to base64.
 *
 * @returns IndexedDB data or null if extraction fails or no databases exist
 */
export async function extractIndexedDB(page: Page): Promise<IndexedDBData | null> {
  try {
    const data = await page.evaluate(
      async ({
        requestTimeoutMs,
        storeReadTimeoutMs,
        maxEntriesPerStore,
      }: {
        requestTimeoutMs: number;
        storeReadTimeoutMs: number;
        maxEntriesPerStore: number;
      }) => {
        function toError(error: unknown, fallback: string): Error {
          if (error instanceof Error) return error;
          if (error && typeof error === 'object' && 'message' in error) {
            const message = (error as { message?: unknown }).message;
            if (typeof message === 'string' && message.length > 0) return new Error(message);
          }
          return new Error(fallback);
        }

        function rejectAfter<T>(
          label: string,
          timeoutMs: number,
          start: (settle: (error?: Error, value?: T) => void) => void
        ): Promise<T> {
          return new Promise<T>((resolve, reject) => {
            let settled = false;
            const timer = setTimeout(() => {
              if (settled) return;
              settled = true;
              reject(new Error(`${label} timed out after ${timeoutMs}ms`));
            }, timeoutMs);

            const settle = (error?: Error, value?: T) => {
              if (settled) return;
              settled = true;
              clearTimeout(timer);
              if (error) {
                reject(error);
                return;
              }
              resolve(value as T);
            };

            start(settle);
          });
        }

        function withTimeout<T>(label: string, timeoutMs: number, promise: Promise<T>): Promise<T> {
          return rejectAfter<T>(label, timeoutMs, (settle) => {
            promise.then(
              (value) => settle(undefined, value),
              (error: unknown) => settle(toError(error, `${label} failed`))
            );
          });
        }

        // indexedDB.databases() is available in all modern Chromium browsers
        if (!indexedDB.databases) return null;

        const dbInfos = await withTimeout(
          'IndexedDB databases()',
          requestTimeoutMs,
          indexedDB.databases()
        );
        if (!dbInfos || dbInfos.length === 0) return null;

        /** Convert binary data to base64 for JSON serialization */
        function serializeValue(value: unknown): unknown {
          if (value instanceof ArrayBuffer) {
            return {
              __type: 'ArrayBuffer',
              __data: btoa(String.fromCharCode(...new Uint8Array(value))),
            };
          }
          if (ArrayBuffer.isView(value)) {
            const buf =
              value.buffer instanceof ArrayBuffer
                ? value.buffer
                : (value.buffer as ArrayBufferLike);
            return {
              __type: 'TypedArray',
              __arrayType: value.constructor.name,
              __data: btoa(
                String.fromCharCode(...new Uint8Array(buf, value.byteOffset, value.byteLength))
              ),
            };
          }
          if (value instanceof Blob) {
            // Skip Blobs — they require async reads and are rare in auth storage
            return { __type: 'Blob', __skipped: true };
          }
          if (value instanceof Date) {
            return { __type: 'Date', __data: value.toISOString() };
          }
          if (Array.isArray(value)) {
            return value.map(serializeValue);
          }
          if (value !== null && typeof value === 'object') {
            const result: Record<string, unknown> = {};
            for (const [k, v] of Object.entries(value)) {
              result[k] = serializeValue(v);
            }
            return result;
          }
          return value;
        }

        const databases: Array<{
          name: string;
          version: number;
          objectStores: Array<{
            name: string;
            keyPath: string | string[] | null;
            autoIncrement: boolean;
            entries: Array<{ key: string | number | Array<string | number>; value: unknown }>;
          }>;
        }> = [];

        function openDatabase(name: string, version: number | undefined): Promise<IDBDatabase> {
          return rejectAfter<IDBDatabase>(`IndexedDB open(${name})`, requestTimeoutMs, (settle) => {
            const request = indexedDB.open(name, version);
            request.onsuccess = () => settle(undefined, request.result);
            request.onerror = () =>
              settle(toError(request.error, `Failed to open IndexedDB database ${name}`));
            // If an upgrade is needed, the version we have is stale — just open what exists
            request.onupgradeneeded = () => {
              request.transaction?.abort();
              settle(new Error('version_mismatch'));
            };
            request.onblocked = () => settle(new Error(`IndexedDB open(${name}) blocked`));
          });
        }

        for (const dbInfo of dbInfos) {
          if (!dbInfo.name) continue;

          try {
            const db = await openDatabase(dbInfo.name, dbInfo.version);

            const objectStores: (typeof databases)[0]['objectStores'] = [];

            for (const storeName of Array.from(db.objectStoreNames)) {
              try {
                const tx = db.transaction(storeName, 'readonly');
                const store = tx.objectStore(storeName);

                type EntryType = (typeof databases)[0]['objectStores'][0]['entries'][0];
                const entries = await rejectAfter<EntryType[]>(
                  `IndexedDB readStore(${dbInfo.name}.${storeName})`,
                  storeReadTimeoutMs,
                  (settle) => {
                    const items: EntryType[] = [];
                    const cursorReq = store.openCursor();
                    cursorReq.onsuccess = () => {
                      const cursor = cursorReq.result;
                      if (cursor) {
                        items.push({
                          key: cursor.key as EntryType['key'],
                          value: serializeValue(cursor.value),
                        });
                        if (items.length >= maxEntriesPerStore) {
                          settle(undefined, items);
                          return;
                        }
                        cursor.continue();
                      } else {
                        settle(undefined, items);
                      }
                    };
                    cursorReq.onerror = () =>
                      settle(
                        toError(cursorReq.error, `Failed to read IndexedDB store ${storeName}`)
                      );
                  }
                );

                objectStores.push({
                  name: storeName,
                  keyPath: store.keyPath as string | string[] | null,
                  autoIncrement: store.autoIncrement,
                  entries,
                });
              } catch {
                // Skip stores that can't be read
              }
            }

            db.close();

            databases.push({
              name: dbInfo.name,
              version: dbInfo.version ?? 1,
              objectStores,
            });
          } catch {
            // Skip databases that can't be opened (version mismatch, etc.)
          }
        }

        return databases.length > 0 ? { databases } : null;
      },
      {
        requestTimeoutMs: IDB_REQUEST_TIMEOUT_MS,
        storeReadTimeoutMs: IDB_STORE_READ_TIMEOUT_MS,
        maxEntriesPerStore: MAX_ENTRIES_PER_STORE,
      }
    );

    if (!data) return null;

    // Check total serialized size to avoid bloating storage state files
    const serialized = JSON.stringify(data);
    if (serialized.length > MAX_TOTAL_SIZE_BYTES) {
      return null;
    }

    return data;
  } catch {
    // Extraction failed (page navigated, closed, etc.) — not critical
    return null;
  }
}

/** Minimal document served to the page while storage state is restored. */
const QUIET_ORIGIN_DOCUMENT =
  '<!doctype html><meta charset="utf-8"><title>storage-restore</title>';

export const QUIET_ORIGIN_NAVIGATION_TIMEOUT_MS = 15_000;

/**
 * Budget for the exit navigation back to the application document.
 *
 * Deliberately larger than {@link QUIET_ORIGIN_NAVIGATION_TIMEOUT_MS}: the entry
 * navigation only has to commit an intercepted 74-byte stub, while this one
 * loads the real application root and waits for it to parse. Reusing the entry
 * budget would fail a connect on a slow-but-healthy application — misreading a
 * slow page as a broken one, which is its own outage shape.
 */
export const QUIET_ORIGIN_EXIT_NAVIGATION_TIMEOUT_MS = TIMEOUTS.NAVIGATE;

/**
 * Raised when the restore itself succeeded but the page could not be returned to
 * the application document. Distinct from a storage-restore failure: the auth
 * state is fine, the page is not, and the remediations differ.
 */
export class QuietOriginDocumentRestoreError extends Error {
  readonly name = 'QuietOriginDocumentRestoreError';

  constructor(origin: string, options?: { cause?: unknown }) {
    super(`Failed to return the page to the application document at ${origin}`, options);
  }
}

/**
 * Run `restore` on a page parked at `origin` with a blank document, so no
 * application script executes for the duration.
 *
 * Storage restoration is origin-scoped and therefore needs a page on the target
 * origin. Loading the real application there boots its SDKs, and any SDK that
 * holds an open IndexedDB connection blocks restoration: Firebase opens
 * `firebaseLocalStorageDb` and `firebase-installations-database` at startup and
 * never closes them, and `indexedDB.deleteDatabase()` cannot complete while any
 * connection is open — it fires `onblocked` and hangs until the request
 * deadline, dropping exactly the authentication databases we came to restore.
 *
 * Serving an empty document keeps the origin scope while guaranteeing nothing
 * else holds a connection. Before returning, the interception is removed and the
 * page is navigated back to `origin`, so the caller is handed the real
 * application document loaded against restored state.
 *
 * That postcondition is part of the contract, not a convenience: consumers
 * downstream of a restore skip navigation when the page URL already matches
 * their target, so a page left on the placeholder reads as a logged-out
 * application at a perfectly legitimate URL. If the document cannot be restored
 * the helper rejects with {@link QuietOriginDocumentRestoreError} — unless
 * `restore` itself failed, in which case that error stays primary and the
 * cleanup failure is attached to it as `cause` rather than discarded.
 *
 * Note the interception is removed before the exit navigation, so a failed exit
 * never leaves the placeholder serving the origin. If `unroute` itself fails the
 * interception *does* survive — every later request in that context would be
 * served the stub — so that failure is always surfaced, never swallowed.
 */
export async function withQuietOriginPage<T>(
  page: Page,
  origin: string,
  restore: () => Promise<T>,
  options: { navigationTimeoutMs?: number; exitNavigationTimeoutMs?: number } = {}
): Promise<T> {
  const handler = (route: Route): Promise<void> =>
    route.fulfill({
      status: 200,
      contentType: 'text/html; charset=utf-8',
      body: QUIET_ORIGIN_DOCUMENT,
    });
  const timeout = options.navigationTimeoutMs ?? QUIET_ORIGIN_NAVIGATION_TIMEOUT_MS;
  const exitTimeout = options.exitNavigationTimeoutMs ?? QUIET_ORIGIN_EXIT_NAVIGATION_TIMEOUT_MS;

  // Tracked explicitly rather than by inspecting a captured error: `throw
  // undefined` is legal, so an `error === undefined` test would misread a
  // genuine failure as success and let the finally block replace it.
  let operationFailed = false;
  let primaryError: unknown;

  const restoreApplicationDocument = async (): Promise<void> => {
    // Unroute first: while the interception is installed, any navigation to the
    // origin is served the stub, so this must succeed for the exit to mean
    // anything.
    await page.unroute('**/*', handler);
    // `domcontentloaded`, not `commit`: at commit the placeholder is gone but
    // the real body may not be parsed yet, and the next caller can read the DOM
    // immediately.
    await page.goto(origin, { waitUntil: 'domcontentloaded', timeout: exitTimeout });
  };

  await page.route('**/*', handler);
  try {
    // The entry navigation only needs the origin scope, not a rendered document.
    await page.goto(origin, { waitUntil: 'commit', timeout });
    return await restore();
  } catch (error) {
    operationFailed = true;
    primaryError = error;
    throw error;
  } finally {
    if (operationFailed) {
      // The primary failure is what the caller must see, but the page may now be
      // stranded on the placeholder — the exact condition this helper exists to
      // prevent. Attach the cleanup failure to the primary error instead of
      // discarding it, so it is still recoverable from logs and diagnostics.
      await restoreApplicationDocument().catch((cleanupError: unknown) => {
        attachQuietOriginCleanupCause(
          primaryError,
          new QuietOriginDocumentRestoreError(origin, { cause: cleanupError })
        );
      });
    } else {
      await restoreApplicationDocument().catch((cleanupError: unknown) => {
        throw new QuietOriginDocumentRestoreError(origin, { cause: cleanupError });
      });
    }
  }
}

/**
 * Record a cleanup failure on the primary error without displacing it.
 *
 * Only fills an empty `cause` — an error that already explains itself keeps its
 * own. A primitive throw (`throw undefined`, `throw 'boom'`) has nowhere to
 * carry the cause, so nothing is attached; the primary value still wins.
 */
function attachQuietOriginCleanupCause(primary: unknown, cleanup: Error): void {
  if (!(primary instanceof Error)) return;
  if (primary.cause !== undefined) return;
  primary.cause = cleanup;
}

/**
 * Restore IndexedDB databases on the current page.
 *
 * Opens each database with the correct version, creates object stores via
 * onupgradeneeded, and populates entries. Handles the case where the database
 * already exists by deleting and recreating it.
 *
 * The caller must ensure no other connection to these databases is open — see
 * {@link withQuietOriginPage}.
 */
export async function restoreIndexedDB(
  page: Page,
  data: IndexedDBData
): Promise<IndexedDBRestoreResult> {
  if (!data.databases || data.databases.length === 0) {
    return {
      restoredDatabases: 0,
      restoredStores: 0,
      restoredEntries: 0,
      skippedDatabases: [],
      skippedStores: [],
    };
  }

  return page.evaluate(
    async ({
      databases,
      requestTimeoutMs,
      transactionTimeoutMs,
    }: {
      databases: IndexedDBData['databases'];
      requestTimeoutMs: number;
      transactionTimeoutMs: number;
    }) => {
      function toError(error: unknown, fallback: string): Error {
        if (error instanceof Error) return error;
        if (error && typeof error === 'object' && 'message' in error) {
          const message = (error as { message?: unknown }).message;
          if (typeof message === 'string' && message.length > 0) return new Error(message);
        }
        return new Error(fallback);
      }

      /** Deserialize base64-encoded binary data back to native types */
      function deserializeValue(value: unknown): unknown {
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          const obj = value as Record<string, unknown>;
          if (obj.__type === 'ArrayBuffer' && typeof obj.__data === 'string') {
            const binary = atob(obj.__data);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            return bytes.buffer;
          }
          if (obj.__type === 'TypedArray' && typeof obj.__data === 'string') {
            const binary = atob(obj.__data);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            // Return as ArrayBuffer — typed array constructor info is for reference
            return bytes.buffer;
          }
          if (obj.__type === 'Date' && typeof obj.__data === 'string') {
            return new Date(obj.__data);
          }
          if (obj.__type === 'Blob' && obj.__skipped) {
            return null; // Blobs were skipped during extraction
          }
          // Recursively deserialize nested objects
          const result: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(obj)) {
            result[k] = deserializeValue(v);
          }
          return result;
        }
        if (Array.isArray(value)) {
          return value.map(deserializeValue);
        }
        return value;
      }

      function rejectAfter<T>(
        label: string,
        timeoutMs: number,
        start: (settle: (error?: Error, value?: T) => void) => void
      ): Promise<T> {
        return new Promise<T>((resolve, reject) => {
          let settled = false;
          const timer = setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new Error(`${label} timed out after ${timeoutMs}ms`));
          }, timeoutMs);

          const settle = (error?: Error, value?: T) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            if (error) {
              reject(error);
              return;
            }
            resolve(value as T);
          };

          start(settle);
        });
      }

      function deleteDatabase(name: string): Promise<void> {
        return rejectAfter<void>(
          `IndexedDB deleteDatabase(${name})`,
          requestTimeoutMs,
          (settle) => {
            const req = indexedDB.deleteDatabase(name);
            req.onsuccess = () => settle();
            req.onerror = () =>
              settle(toError(req.error, `Failed to delete IndexedDB database ${name}`));
            req.onblocked = () => {
              // A blocked request may continue once another context closes its connection.
              // Keep waiting for success/error; rejectAfter remains the hard deadline.
            };
          }
        );
      }

      function openDatabase(dbData: IndexedDBData['databases'][number]): Promise<IDBDatabase> {
        return rejectAfter<IDBDatabase>(
          `IndexedDB open(${dbData.name})`,
          requestTimeoutMs,
          (settle) => {
            const request = indexedDB.open(dbData.name, dbData.version);
            request.onupgradeneeded = () => {
              const db = request.result;
              for (const storeData of dbData.objectStores) {
                if (!db.objectStoreNames.contains(storeData.name)) {
                  db.createObjectStore(storeData.name, {
                    keyPath: storeData.keyPath ?? undefined,
                    autoIncrement: storeData.autoIncrement,
                  });
                }
              }
            };
            request.onsuccess = () => settle(undefined, request.result);
            request.onerror = () =>
              settle(toError(request.error, `Failed to open IndexedDB database ${dbData.name}`));
            request.onblocked = () => {
              // Version upgrades can unblock without intervention. The existing request
              // deadline prevents an abandoned connection from hanging restoration.
            };
          }
        );
      }

      function waitForTransaction(tx: IDBTransaction, storeName: string): Promise<void> {
        return rejectAfter<void>(
          `IndexedDB transaction(${storeName})`,
          transactionTimeoutMs,
          (settle) => {
            tx.oncomplete = () => settle();
            tx.onerror = () =>
              settle(toError(tx.error, `IndexedDB transaction failed for ${storeName}`));
            tx.onabort = () =>
              settle(toError(tx.error, `IndexedDB transaction aborted for ${storeName}`));
          }
        );
      }

      const result: IndexedDBRestoreResult = {
        restoredDatabases: 0,
        restoredStores: 0,
        restoredEntries: 0,
        skippedDatabases: [],
        skippedStores: [],
      };

      for (const dbData of databases) {
        try {
          // Delete existing database first to avoid version conflicts
          await deleteDatabase(dbData.name);

          // Open with the correct version to trigger onupgradeneeded
          const db = await openDatabase(dbData);

          // Populate entries
          for (const storeData of dbData.objectStores) {
            if (storeData.entries.length === 0) continue;
            if (!db.objectStoreNames.contains(storeData.name)) continue;

            try {
              const tx = db.transaction(storeData.name, 'readwrite');
              const store = tx.objectStore(storeData.name);

              for (const entry of storeData.entries) {
                const value = deserializeValue(entry.value);
                if (storeData.keyPath) {
                  store.put(value);
                } else {
                  store.put(value, entry.key);
                }
              }

              await waitForTransaction(tx, storeData.name);
              result.restoredStores++;
              result.restoredEntries += storeData.entries.length;
            } catch (error) {
              // Skip stores that fail to populate
              result.skippedStores.push({
                database: dbData.name,
                store: storeData.name,
                error: toError(error, `Failed to restore IndexedDB store ${storeData.name}`)
                  .message,
              });
            }
          }

          db.close();
          result.restoredDatabases++;
        } catch (error) {
          // Skip databases that fail to restore
          result.skippedDatabases.push({
            name: dbData.name,
            error: toError(error, `Failed to restore IndexedDB database ${dbData.name}`).message,
          });
        }
      }

      return result;
    },
    {
      databases: data.databases,
      requestTimeoutMs: IDB_REQUEST_TIMEOUT_MS,
      transactionTimeoutMs: IDB_TRANSACTION_TIMEOUT_MS,
    }
  );
}

export function getIndexedDBRestoreExpectation(data: IndexedDBData): IndexedDBRestoreExpectation {
  return data.databases.reduce<IndexedDBRestoreExpectation>(
    (total, database) => {
      total.databases += 1;
      for (const store of database.objectStores) {
        if (store.entries.length === 0) continue;
        total.stores += 1;
        total.entries += store.entries.length;
      }
      return total;
    },
    { databases: 0, stores: 0, entries: 0 }
  );
}

/**
 * Bound the sequential whole-restore operation without racing valid per-operation deadlines.
 * Each database is deleted and opened, then every populated store runs one transaction.
 */
export function getIndexedDBRestoreWatchdogMs(data: IndexedDBData): number {
  const expectation = getIndexedDBRestoreExpectation(data);
  const requestOperations = expectation.databases * 2;
  const calculatedMs =
    requestOperations * IDB_RESTORE_REQUEST_TIMEOUT_MS +
    expectation.stores * IDB_RESTORE_TRANSACTION_TIMEOUT_MS +
    IDB_RESTORE_WATCHDOG_BUFFER_MS;
  return Math.min(IDB_RESTORE_WATCHDOG_MAX_MS, Math.max(IDB_RESTORE_WATCHDOG_MIN_MS, calculatedMs));
}

/** Fail required state closed when none of its expected localStorage survived app startup. */
export function assertRequiredLocalStorageRestored(args: {
  required: boolean;
  expectedEntries: number;
  matchedEntries: number;
}): void {
  if (args.required && args.expectedEntries > 0 && args.matchedEntries === 0) {
    throw new Error(
      `Required localStorage authentication state restoration incomplete: matched 0/${args.expectedEntries} entries`
    );
  }
}

/** Enforce the auth-state invariant at the boundary that restores browser state. */
export function assertCompleteIndexedDBRestore(
  data: IndexedDBData,
  result: IndexedDBRestoreResult
): void {
  const expectation = getIndexedDBRestoreExpectation(data);
  if (
    result.skippedDatabases.length > 0 ||
    result.skippedStores.length > 0 ||
    result.restoredDatabases !== expectation.databases ||
    result.restoredStores !== expectation.stores ||
    result.restoredEntries !== expectation.entries
  ) {
    throw new IndexedDBRestoreIncompleteError(expectation, result);
  }
}
